import { useState, useEffect, useRef, useContext, useMemo } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { Box, Grid } from "@mui/material";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { LocaleContext } from "../../../routing/LangRouter.js";
import { convertToGregorian } from "../../../utils/date.js";
import { DateObject } from "react-multi-date-picker";

//import constants
import { IS_MOBILE } from "../../../constants/breakpoints.js";

//import customized hooks
import useCityList from "../../../hooks/useCityList.js";
import useDebouncedInputChange from "../../../hooks/useDebouncedInputChange .js";

//import general components
import { BaseButton } from "../../common/base-button/base-button.jsx";
import AutoCompleteComponent from "../../common/base-select/autocomplete-component.js.jsx";
import BaseTextField from "../../common/base-text-field/base-text-field.jsx";
import Icon from "../../common/base-icon/icon.jsx";
import SingleDatePicker from "../../common/single-date-picker/single-date-picker.jsx";

//import images and icons
import { ArrowSwapHorizontal, CloseCircle, Location, SearchNormal } from "iconsax-react";
import { saveTicketSelectedFilters } from "../../../store/actions/index.js";

function SearchFormContent({ dispatch, openSearchForm, setRequestFlag, setUid, busRoute }) {
    //general variables
    const { control } = useForm();
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const destinationRef = useRef();
    const rangeDatePickerRef = useRef();
    const { locale } = useContext(LocaleContext);
    const { t } = useTranslation();

    //handle filter cancellation
    const resetFilters = () => {
        dispatch(saveTicketSelectedFilters(null));
    };

    //sessionStorage variables
    const busInformation = useMemo(() => {
        return JSON.parse(sessionStorage.getItem("busInformation"));
    }, []);
    const [visibleAutocomplete, setVisibleAutocomplete] = useState({ departure: IS_MOBILE ? false : true, destination: IS_MOBILE ? false : true });
    const handleShowAutocompplete = name => {
        setVisibleAutocomplete({ [name]: true });
    };

    //handle destiantion city change
    const onDepartureInputChange = useDebouncedInputChange(input => {
        getDepartureCities(input);
    }, 1000);

    const onDestinationInputChange = useDebouncedInputChange(input => {
        getDestinationCities(input);
    }, 1000);

    //request city list. inputs:foreign, all, flight, accommodation, bus
    const { cities: departureCities, isCityLoading: departureLoading, getCities: getDepartureCities } = useCityList(false, false, false, false, true);
    const {
        cities: destinationCities,
        isCityLoading: destinationLoading,
        getCities: getDestinationCities,
    } = useCityList(false, false, false, false, true);
    useEffect(() => {
        if (location.pathname === `/${locale}/bus-search`) {
            getDepartureCities();
            getDestinationCities();
        }
    }, [location.pathname]);

    //handle departure and destination change
    const [selectedDeparture, setSelectedDeparture] = useState(null);
    const [selectedDestination, setSelectedDestination] = useState(null);

    const handleCityChange = (type, value) => {
        if (type === "departure") {
            setSelectedDeparture(value);
            if (IS_MOBILE) setVisibleAutocomplete({ departure: false, destination: selectedDestination || true });
            if (!selectedDestination) destinationRef?.current?.focus();
        } else {
            setSelectedDestination(value);
            if (IS_MOBILE) setVisibleAutocomplete({ departure: false, destination: false });
            if (!departureDate) rangeDatePickerRef?.current?.focus();
        }

        setUid(null);
    };

    //default value
    useEffect(() => {
        //departute & destination
        if (searchParams.get("action") === "edit") {
            setSelectedDeparture(busInformation?.departure);
            setSelectedDestination(busInformation?.destination);
        }
    }, [openSearchForm]);

    //handle swap change
    const handleSwapChange = () => {
        setSelectedDestination(selectedDeparture);
        setSelectedDeparture(selectedDestination);
    };

    //handle hotel date change
    const initialStartDate = busInformation?.departureDate;
    const [departureDate, setDepartureDate] = useState();
    const dateChange = date => {
        setDepartureDate(date);
        setUid(null);
    };

    useEffect(() => {
        setDepartureDate(new DateObject(new Date(busInformation?.departureDate)));
    }, []);

    //submit form
    const submitSearchForm = event => {
        event.preventDefault();
        setUid(null);
        resetFilters();
        if (!selectedDestination || !selectedDeparture || !departureDate) return toast.error(t("errorMessage2"), { toastId: "custom-id-yes" });

        const convertedStartDate = convertToGregorian(departureDate);

        //set sessionStorage
        const newBusInformation = {
            departure: selectedDeparture,
            destination: selectedDestination,
            departureDate: convertedStartDate?.toISOString().split("T")[0] || busInformation?.departureDate,
        };
        sessionStorage.setItem("busInformation", JSON.stringify(newBusInformation));

        searchParams.delete("action");
        setSearchParams(searchParams);

        if (!busRoute) setRequestFlag(true);
        if (!!busRoute) navigate(busRoute);
    };

    return (
        <Grid
            component="form"
            className="hotel-search-form"
            container
            item
            xs={12}
            rowGap={2.5}
            columnGap={2}
            alignItems="center"
            onSubmit={submitSearchForm}
        >
            <Grid className="flight-search-form__select" container item xs={12} md={6} justifyContent="space-between" alignItems="center">
                {IS_MOBILE && (
                    <Grid container item xs={5.8} md={5.4}>
                        <BaseTextField
                            label={t("departure")}
                            value={locale === "en" ? selectedDeparture?.english_name : selectedDeparture?.name}
                            onClick={() => handleShowAutocompplete("departure")}
                            radius="10px"
                            icon={
                                <span onClick={e => e.stopPropagation()} style={{ cursor: "default", opacity: 0.5 }} tabIndex={-1}>
                                    <Icon name="DepartureIcon" width="0.8rem" />
                                </span>
                            }
                            readOnly={true}
                        />
                    </Grid>
                )}
                {visibleAutocomplete?.departure && (
                    <Grid className="flight-search-form__autocomplete" container item md={5.4} justifyContent="space-between" alignItems="center">
                        <Grid container item xs={IS_MOBILE ? 10.5 : 12}>
                            <AutoCompleteComponent
                                label={t("from(Noun)")}
                                options={departureCities}
                                name="departure"
                                defaultValue={selectedDeparture}
                                handleChange={value => handleCityChange("departure", value)}
                                setInputValue={onDepartureInputChange}
                                control={control}
                                loading={departureLoading}
                                width={350}
                                icon={<Icon name="DepartureIcon" width="0.8rem" />}
                                open={IS_MOBILE ? true : false}
                                type="other"
                            />
                        </Grid>
                        {IS_MOBILE && (
                            <Grid container item xs={1}>
                                <CloseCircle
                                    color="var(--dark-color-60)"
                                    onClick={() => setVisibleAutocomplete({ departure: false, destination: false })}
                                />
                            </Grid>
                        )}
                    </Grid>
                )}

                <Box className="flight-search-form__swap">
                    <ArrowSwapHorizontal
                        size={IS_MOBILE ? "18" : "24"}
                        color="var(--light-color)"
                        onClick={selectedDeparture && selectedDestination && handleSwapChange}
                    />
                </Box>

                {IS_MOBILE && (
                    <Grid container item xs={5.8} md={5.4}>
                        <BaseTextField
                            label={t("destination")}
                            value={locale === "en" ? selectedDestination?.english_name : selectedDestination?.name}
                            onClick={() => handleShowAutocompplete("destination")}
                            radius="10px"
                            icon={
                                <span onClick={e => e.stopPropagation()} style={{ cursor: "default", opacity: 0.5 }} tabIndex={-1}>
                                    <Location size="20" color="var(--teal-color-18)" />
                                </span>
                            }
                            readOnly={true}
                        />
                    </Grid>
                )}
                {visibleAutocomplete?.destination && (
                    <Grid className="flight-search-form__autocomplete" container item md={5.4} justifyContent="space-between" alignItems="center">
                        <Grid container item xs={IS_MOBILE ? 10.5 : 12}>
                            <AutoCompleteComponent
                                label={t("to(Noun)")}
                                options={destinationCities}
                                name="destination"
                                defaultValue={selectedDestination}
                                handleChange={value => handleCityChange("destination", value)}
                                setInputValue={onDestinationInputChange}
                                innerRef={destinationRef}
                                control={control}
                                loading={destinationLoading}
                                width={350}
                                icon={<Location size="20" color="var(--teal-color-18)" />}
                                open={IS_MOBILE ? true : false}
                                type="other"
                            />
                        </Grid>
                        {IS_MOBILE && (
                            <Grid container item xs={1}>
                                <CloseCircle
                                    color="var(--dark-color-60)"
                                    onClick={() => setVisibleAutocomplete({ departure: false, destination: false })}
                                />
                            </Grid>
                        )}
                    </Grid>
                )}
            </Grid>

            <Grid container item xs={12} md={3}>
                <SingleDatePicker
                    label={t("departureDate")}
                    name="date"
                    size="medium"
                    handleChange={dateChange}
                    defaultValue={departureDate}
                    minDate={new Date()}
                    control={control}
                    isPersianCalendar={locale === "fa"}
                />
            </Grid>

            <Grid container item md={1.5}>
                <BaseButton
                    text={`${t("search")}`}
                    color="primary-10"
                    size="large"
                    radius="radius-20"
                    type="submit"
                    leftIcon={<SearchNormal size="24" color="var(--primary-color)" />}
                />
            </Grid>
        </Grid>
    );
}

export default connect()(SearchFormContent);

